import { type CSSProperties, type RefObject, useRef, useState } from "react";
import { keyframes, styled } from "@pigment-css/react";
import type { Meeting } from "@acto/taskpane/core/domain";
import { CardSurface } from "@acto/taskpane/field-sales-2/components/patterns/CustomerCard";
import { FrontCard } from "./FrontCard";

const StackWrap = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  // Reserve room below for the peeking cards without inflating their height: a
  // percentage height on the absolutely-positioned behind cards resolves against this
  // element's padding box, so this gap must be a margin, not padding.
  marginBottom: theme.fieldSalesTwo.spacing[32],
}));

const DeckCard = styled("div")<{ front?: boolean }>(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  transformOrigin: "bottom center",
  variants: [
    {
      // The front card holds its position — no transform transition — so an arriving
      // card appears in place (the snap from its peek transform is covered by the
      // departing LeavingCard) instead of visibly traveling up from the peek.
      props: { front: true },
      style: {
        transition: `opacity ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}, filter ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}`,
      },
    },
    {
      // Cards behind slide + scale into their new peek position, and stretch to the
      // front's height (set by the Sizer) so the blank surface fills the deck.
      props: { front: false },
      style: {
        height: "100%",
        transition: `transform ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}, filter ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}`,
      },
    },
  ],
}));

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

// Transient clone of the card that just left the front, layered above the new front
// (which renders fully in place with NO animation of its own). Two sequenced fades keep
// the top card visually pinned while its content swaps: first the old card fades out
// over the opaque blank cover (old text → blank), then the whole clone fades away
// (blank → new text). Never simultaneous — overlapping them stacks both cards' text on
// top of each other.
// A clone (not the real deck element) because the real element may simultaneously be
// needed at its new peek slot — this is what keeps the effect working for batches
// smaller than the deck's buffer window.
const LeavingCard = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  // The stack's height (via the Sizer) is the INCOMING front card's, so the cover
  // always spans the card it must hide — the leaving clone alone can be shorter.
  height: "100%",
  zIndex: 11,
  pointerEvents: "none",
  animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard} ${theme.fieldSalesTwo.motion.duration.base} forwards`,
}));

// Opaque stand-in surface behind the fading old card: identical tokens, so the fade
// reads as the text dissolving while the card itself stays put.
const LeavingCover = styled(CardSurface)({
  position: "absolute",
  inset: 0,
});

const LeavingContent = styled("div")(({ theme }) => ({
  position: "relative",
  animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard} forwards`,
}));

// Hidden in-flow copy of the front card so the deck container takes its height (the
// real cards are all absolute). The accordion grows it naturally — no measuring.
const Sizer = styled("div")({
  visibility: "hidden",
  width: "100%",
  pointerEvents: "none",
});

// Blank surface for cards behind the front: same visual surface tokens as a real card
// but no content and a uniform height (fills the absolutely-positioned DeckCard, which
// is stretched to the front's height). This gives a clean, even peek with no chevron
// and no variable height from title wrapping.
const BehindSurface = styled(CardSurface)({
  width: "100%",
  height: "100%",
});

const DECK_BEHIND_SCALE = 0.04;
const DECK_OFFSET_PX = 10;

// Top card full; cards behind are scaled down, nudged down, and darkened via
// brightness (so it's the real card, X% darker).
// The entering buffer (below the last visible peek) sits at opacity 0 so mount/unmount
// during the rotation is invisible.
function slotStyle(off: number, visibleCount: number): CSSProperties {
  const scale = 1 - off * DECK_BEHIND_SCALE;
  return {
    transform: `translateY(${off * DECK_OFFSET_PX}px) scale(${scale})`,
    opacity: off < visibleCount ? 1 : 0,
    filter: `brightness(${1 - Math.min(off * 0.05, 0.02)})`,
    zIndex: 10 - off,
  };
}

interface CardStackProps {
  items: Meeting[];
  safeIndex: number;
  meeting: Meeting;
  /** Suppress the card's date badge (ad-hoc recap has no meeting date). */
  hideDate?: boolean;
  /** Show the scheduled time (HH:mm) in the badge. */
  showBadgeTime?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  cardRef: RefObject<HTMLElement | null>;
  flightHidden: "card" | "pill" | null;
}

export function CardStack({
  items,
  safeIndex,
  meeting,
  hideDate,
  showBadgeTime,
  expanded,
  onToggleExpand,
  cardRef,
  flightHidden,
}: CardStackProps) {
  // Derived-state trigger: when a different meeting reaches the front, spawn the
  // leaving clone of the one it replaced (guarded render-phase setState). `expanded`
  // is captured so the clone matches the incoming card's height — navigation resets it
  // in the same batch, so both cards agree on the collapsed state.
  const [leaving, setLeaving] = useState<{ meeting: Meeting; expanded: boolean; key: number } | null>(null);
  const prevMeetingRef = useRef(meeting);
  const leavingKeyRef = useRef(0);
  if (prevMeetingRef.current.id !== meeting.id) {
    leavingKeyRef.current += 1;
    setLeaving({ meeting: prevMeetingRef.current, expanded, key: leavingKeyRef.current });
    prevMeetingRef.current = meeting;
  }

  return (
    <StackWrap style={{ visibility: flightHidden === "card" ? "hidden" : "visible" }}>
      {/* Hidden in-flow copy sets the stack's height (real cards are all absolute). */}
      <Sizer aria-hidden="true">
        {/* FrontCard (not plain CustomerCard) so the hidden sizer fetches the same rich client
            and measures the true expanded height — otherwise the input below under-shifts. */}
        <FrontCard
          meeting={meeting}
          hideDate={hideDate}
          showBadgeTime={showBadgeTime}
          expanded={expanded}
          onToggleExpand={() => undefined}
        />
      </Sizer>
      {(() => {
        const visibleCount = Math.min(items.length, 3); // top + up to 2 behind
        const offsets: number[] = [];
        for (let s = 0; s < visibleCount; s++) offsets.push(s);
        if (items.length > visibleCount) offsets.push(visibleCount); // entering buffer (faded)
        return offsets.map((off) => {
          const m = items[(((safeIndex + off) % items.length) + items.length) % items.length];
          const isFront = off === 0;
          return (
            <DeckCard key={m.id} front={isFront} style={slotStyle(off, visibleCount)}>
              {isFront ? (
                <FrontCard
                  ref={cardRef}
                  meeting={m}
                  hideDate={hideDate}
                  showBadgeTime={showBadgeTime}
                  expanded={expanded}
                  onToggleExpand={onToggleExpand}
                />
              ) : (
                // Behind cards: blank uniform surface (no content, no chevron, no
                // variable height) so they peek cleanly below the front.
                <BehindSurface aria-hidden="true" />
              )}
            </DeckCard>
          );
        });
      })()}
      {leaving && (
        <LeavingCard
          key={leaving.key}
          aria-hidden="true"
          // The inner content fade bubbles its animationend too — only the outer
          // (delayed, last-to-finish) fade may unmount the clone.
          onAnimationEnd={(e) => e.target === e.currentTarget && setLeaving(null)}
        >
          <LeavingCover />
          <LeavingContent>
            <FrontCard
              meeting={leaving.meeting}
              hideDate={hideDate}
              showBadgeTime={showBadgeTime}
              expanded={leaving.expanded}
              onToggleExpand={() => undefined}
            />
          </LeavingContent>
        </LeavingCard>
      )}
    </StackWrap>
  );
}
